import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
    PlusIcon,
    FolderIcon,
    ArrowDownTrayIcon,
    EyeIcon,
    TrashIcon,
} from '@heroicons/react/24/outline';
import { Button } from '../../../components/buttons/Button';
import { ExpandableText } from '../../../components/expandableText/ExpandableText';
import { ResourceFormDrawer } from './ResourceFormDrawer';
import { fetchCourseResources, deleteCourseResource } from '../../../redux/actions/resources';
import {
    COURSE_RESOURCE_LIMIT,
    formatFileSize,
    getAttachmentKind,
    ATTACHMENT_KIND_LABELS,
    canPreviewInBrowser,
} from '../../../common/resourceUploads';
import { formatDateTime } from '../../../common/formatDateText';
import { capitalizarPalabras } from '../../../common/upperCaseWord';
import './resources-panel.css';

const kindLabel = (mimeType) => ATTACHMENT_KIND_LABELS[getAttachmentKind(mimeType)] || 'Archivo';

const ResourceCard = ({ resource, canManage, onDelete, deleting }) => {
    const [confirming, setConfirming] = useState(false);
    const canPreview = canPreviewInBrowser(resource.mimeType) && resource.url;

    return (
        <article className="resources-panel__card">
            <div className="resources-panel__card-top">
                <span className="resources-panel__kind">{kindLabel(resource.mimeType)}</span>
                <h3 className="resources-panel__card-title">
                    {capitalizarPalabras(resource.title || resource.fileName)}
                </h3>
                <p className="resources-panel__file-name" translate="no">{resource.fileName}</p>
                {resource.description ? <ExpandableText text={resource.description} /> : null}
            </div>

            <dl className="resources-panel__meta">
                <div>
                    <dt>Tamaño</dt>
                    <dd>{formatFileSize(resource.size) || '—'}</dd>
                </div>
                <div>
                    <dt>Subido</dt>
                    <dd>{formatDateTime(resource.createdAt)}</dd>
                </div>
            </dl>

            <div className="resources-panel__actions">
                {canPreview ? (
                    <a
                        href={resource.url}
                        target="_blank"
                        rel="noreferrer"
                        className="resources-panel__link"
                    >
                        <EyeIcon className="w-4 h-4" aria-hidden="true" />
                        Ver
                    </a>
                ) : null}
                {resource.url ? (
                    <a
                        href={resource.url}
                        download={resource.fileName}
                        className="resources-panel__link"
                    >
                        <ArrowDownTrayIcon className="w-4 h-4" aria-hidden="true" />
                        Descargar
                    </a>
                ) : null}
                {canManage ? (
                    confirming ? (
                        <span className="resources-panel__confirm">
                            <button type="button" onClick={() => onDelete(resource)} disabled={deleting}>
                                Eliminar
                            </button>
                            <button type="button" onClick={() => setConfirming(false)}>
                                Cancelar
                            </button>
                        </span>
                    ) : (
                        <button
                            type="button"
                            className="resources-panel__delete"
                            onClick={() => setConfirming(true)}
                            aria-label={`Eliminar ${resource.title || resource.fileName}`}
                        >
                            <TrashIcon className="w-4 h-4" aria-hidden="true" />
                        </button>
                    )
                ) : null}
            </div>
        </article>
    );
};

export const ResourcesPanel = ({ courseId }) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [role, setRole] = useState(null);
    const [resources, setResources] = useState([]);
    const [used, setUsed] = useState(0);
    const [limit, setLimit] = useState(COURSE_RESOURCE_LIMIT);
    const [formOpen, setFormOpen] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const remaining = Math.max(0, limit - used);
    const canManage = role === 'teacher';

    const loadResources = async () => {
        if (!courseId) return;
        setLoading(true);
        setError(null);
        try {
            const data = await dispatch(fetchCourseResources(courseId));
            setRole(data.role);
            setResources(data.resources || []);
            setUsed(data.used ?? (data.resources || []).length);
            setLimit(data.limit ?? COURSE_RESOURCE_LIMIT);
        } catch (err) {
            setError(err?.response?.data?.message || 'No se pudieron cargar los recursos.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadResources();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courseId]);

    const handleCreated = (data) => {
        if (data?.resources) {
            setResources((prev) => [...data.resources, ...prev]);
        }
        if (typeof data?.used === 'number') setUsed(data.used);
        if (typeof data?.limit === 'number') setLimit(data.limit);
    };

    const handleDelete = async (resource) => {
        setDeletingId(resource._id);
        try {
            const data = await dispatch(deleteCourseResource(resource._id));
            setResources((prev) => prev.filter((item) => item._id !== resource._id));
            if (typeof data?.used === 'number') setUsed(data.used);
            toast.success('Recurso eliminado');
        } catch (err) {
            toast.error(err?.response?.data?.message || 'No se pudo eliminar el recurso.');
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <div className="resources-panel">
            <div className="resources-panel__top">
                <div className="resources-panel__heading">
                    <h2 className="resources-panel__title">Recursos</h2>
                    <p className="resources-panel__usage" aria-live="polite">
                        Recursos utilizados: {used} / {limit}
                        <span> · {remaining} disponibles</span>
                    </p>
                </div>
                {canManage ? (
                    <Button
                        variant="gradient"
                        fullWidth
                        className="resources-panel__create"
                        icon={<PlusIcon className="w-4 h-4" aria-hidden="true" />}
                        onClick={() => {
                            if (remaining <= 0) {
                                toast.error('Este curso ya alcanzó el máximo de 50 recursos.');
                                return;
                            }
                            setFormOpen(true);
                        }}
                    >
                        Nuevo recurso
                    </Button>
                ) : null}
            </div>

            <div className="resources-panel__list">
                {loading ? (
                    <div className="resources-panel__skeleton" aria-label="Cargando recursos">
                        {[0, 1, 2].map((i) => (
                            <div key={i} className="resources-panel__skeleton-card" />
                        ))}
                    </div>
                ) : error ? (
                    <div className="resources-panel__empty">
                        <p className="resources-panel__empty-text">{error}</p>
                        <Button variant="outline" size="sm" onClick={loadResources}>Reintentar</Button>
                    </div>
                ) : resources.length === 0 ? (
                    <div className="resources-panel__empty">
                        <FolderIcon className="resources-panel__empty-icon" aria-hidden="true" />
                        <p className="resources-panel__empty-text">
                            {canManage
                                ? 'Todavía no hay recursos en este curso. Sube material de estudio, PDFs o ejercicios.'
                                : 'Todavía no hay recursos disponibles en este curso.'}
                        </p>
                    </div>
                ) : (
                    <div className="resources-panel__grid course-tab-stagger">
                        {resources.map((resource) => (
                            <ResourceCard
                                key={resource._id}
                                resource={resource}
                                canManage={canManage}
                                onDelete={handleDelete}
                                deleting={deletingId === resource._id}
                            />
                        ))}
                    </div>
                )}
            </div>

            <ResourceFormDrawer
                isOpen={formOpen}
                onClose={() => setFormOpen(false)}
                courseId={courseId}
                remaining={remaining}
                onCreated={handleCreated}
            />
        </div>
    );
};
